import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useReporter } from '../../reporter';
import BoxImage from '../../images/mx-ollie-box.png';
import BeefPack from '../../images/our-food/new-beef-pack.png';
import TurkeyPack from '../../images/our-food/new-turkey-pack.png';
import ChickenPack from '../../images/our-food/new-chicken-pack.png';
import DryChickenBag from '../../images/our-food/chicken-baked-bag.png';
import DryBeefBag from '../../images/our-food/beef-baked-bag.png';
import LambPack from '../../images/our-food/new-lamb-pack.png';
import { Heading, Tooltip, VerticalDivider, VisuallyHidden } from '../../atoms';
import { ResponsiveImage } from '../../components';
import { userClickEditFreshRecipe, userClickEditBakedRecipe } from './events';
import {
  Container,
  DateInfo,
  ArrivalText,
  IncludedText,
  SubText,
  RecipeCount,
  EditAnchor,
  ChangeNotAllowedText,
  ChangeNotAllowedTip,
  Contents,
  RecipeImageContainer,
  RecipeImage,
} from './styles.js';

const recipeImageMap = {
  fresh: {
    beef: BeefPack,
    chicken: ChickenPack,
    turkey: TurkeyPack,
    lamb: LambPack,
  },
  dry: {
    beef: DryBeefBag,
    chicken: DryChickenBag,
  },
};

export const NextBox = ({
  arrivalDate,
  lockInDate,
  cadence,
  recipes,
  editFreshHref,
  editBakedHref,
  headline,
  totalPacks,
  totalBags,
}) => {
  const reporter = useReporter();
  const hasFresh = Boolean(recipes.fresh.length);
  const hasBaked = Boolean(recipes.dry.length);
  // TODO clean this up?
  let subText;
  if (hasFresh && hasBaked)
    subText =
      cadence +
      ` Week Supply (${totalPacks} packs of fresh, ${totalBags} bags of baked)`;
  else if (hasFresh)
    subText = cadence + ` Week Supply (${totalPacks} packs of fresh)`;
  else if (hasBaked)
    subText = cadence + ` Week Supply (${totalBags} bags of baked)`;
  const onEditFreshRecipeHandler = () => {
    reporter.tag(userClickEditFreshRecipe());
  };
  const onEditBakedRecipeHandler = () => {
    reporter.tag(userClickEditBakedRecipe());
  };
  return (
    <>
      <Heading
        headingText={headline}
        headingLevel="h2"
        typography={{ desktop: 'heading2', mobile: 'heading1' }}
      />
      <Container>
        <DateInfo>
          <ResponsiveImage
            src={BoxImage}
            alt="ollie box"
            height={115}
            width={133}
          />
          <ArrivalText>Next box arrives on {arrivalDate}</ArrivalText>
          {lockInDate ? (
            <SubText>You can make changes until {lockInDate}</SubText>
          ) : (
            <Tooltip
              placement="top"
              trigger={props => (
                <ChangeNotAllowedText {...props}>
                  You can no longer make changes to this box, shipment is in
                  progress.
                </ChangeNotAllowedText>
              )}
            >
              <ChangeNotAllowedTip>
                If you have questions about this box, please contact our Canine
                Care team at{' '}
                <a href="mailto:hello@myollie.com">hello@myollie.com</a>
              </ChangeNotAllowedTip>
            </Tooltip>
          )}
        </DateInfo>
        <VerticalDivider />
        <Contents>
          <IncludedText>What's included in this box</IncludedText>
          <SubText>{subText}</SubText>
          {hasFresh && (
            <>
              <RecipeCount>
                Fresh Recipes ({recipes.fresh.length})
                {lockInDate ? (
                  <Link href={editFreshHref} passHref>
                    <EditAnchor onClick={onEditFreshRecipeHandler}>
                      Edit
                      <VisuallyHidden>Fresh Recipes</VisuallyHidden>
                    </EditAnchor>
                  </Link>
                ) : null}
              </RecipeCount>
              <RecipeImageContainer>
                {recipes.fresh.map((recipe, index) => (
                  <RecipeImage key={index}>
                    <ResponsiveImage
                      src={recipeImageMap.fresh[recipe]}
                      alt={`${recipe} pack`}
                      height={72}
                      width={111}
                    />
                  </RecipeImage>
                ))}
              </RecipeImageContainer>
            </>
          )}
          {hasBaked && (
            <>
              <RecipeCount>
                Baked Recipes ({recipes.dry.length})
                {lockInDate ? (
                  <Link href={editBakedHref} passHref>
                    <EditAnchor onClick={onEditBakedRecipeHandler}>
                      Edit
                      <VisuallyHidden>Baked Recipes</VisuallyHidden>
                    </EditAnchor>
                  </Link>
                ) : null}
              </RecipeCount>
              <RecipeImageContainer>
                {recipes.dry.map((recipe, index) => (
                  <RecipeImage key={index}>
                    <ResponsiveImage
                      src={recipeImageMap.dry[recipe]}
                      alt={`${recipe} pack`}
                      height={104}
                      width={100}
                    />
                  </RecipeImage>
                ))}
              </RecipeImageContainer>
            </>
          )}
        </Contents>
      </Container>
    </>
  );
};

NextBox.propTypes = {
  arrivalDate: PropTypes.string.isRequired,
  lockInDate: PropTypes.string,
  cadence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  recipes: PropTypes.shape({
    fresh: PropTypes.arrayOf(
      PropTypes.oneOf(['beef', 'chicken', 'lamb', 'turkey']),
    ),
    dry: PropTypes.arrayOf(
      PropTypes.oneOf(['beef', 'chicken', 'lamb', 'turkey']),
    ),
  }).isRequired,
  editFreshHref: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  editBakedHref: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  headline: PropTypes.string.isRequired,
  totalPacks: PropTypes.number.isRequired,
  totalBags: PropTypes.number.isRequired,
};
